// API client for finance app
import {
  Expense,
  GenerateMealRequest,
  GenerateMealResponse,
  Income,
  Debt,
  DebtPayment,
  Goal,
  Ingredient,
  Supply,
  MealPlan,
  MealPlanEntry,
  MealTemplate,
  ShoppingList,
  ShoppingListItem,
} from "../../lib/types";
import { debugThrottled } from "../../lib";
import { demoService } from "../demo/demoService";
import { demoAPI } from "./demoApi";
import { supabase } from "../supabase/client";
import { getCachedAccessToken } from "../supabase/authStorage";
import { isAbortError } from "../utils/abort";

// Base API URL - use environment variable or fallback to relative path
const resolveApiUrl = () => {
  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return "/api";
    }
  }
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "/api";
};

export const API_URL = resolveApiUrl();

export class DuplicateExpenseApiError extends Error {
  duplicateExpense?: Partial<Expense>;

  constructor(message: string, duplicateExpense?: Partial<Expense>) {
    super(message);
    this.name = "DuplicateExpenseApiError";
    this.duplicateExpense = duplicateExpense;
  }
}

let refreshSessionPromise: Promise<any | null> | null = null;

const getRefreshedSessionSingleFlight = async () => {
  if (!refreshSessionPromise) {
    refreshSessionPromise = supabase.auth
      .refreshSession()
      .then(({ data }) => data?.session ?? null)
      .catch((error) => {
        if (isAbortError(error)) {
          console.debug("[DEBUG] refreshSession aborted", error);
        } else {
          console.warn("[DEBUG] refreshSession failed", error);
        }
        return null;
      })
      .finally(() => {
        refreshSessionPromise = null;
      });
  }
  return refreshSessionPromise;
};

// Helper function to get authentication headers
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  if (demoService.isDemo()) {
    debugThrottled(
      "authHeaders:demo",
      1500,
      "[DEBUG] getAuthHeaders demo mode",
      { isDemo: true }
    );
    return {
      "Content-Type": "application/json",
    };
  }
  debugThrottled("authHeaders:start", 1500, "[DEBUG] getAuthHeaders start");
  let session = null;
  try {
    const {
      data: { session: activeSession },
    } = await supabase.auth.getSession();
    session = activeSession;
    if (!session) {
      session = await getRefreshedSessionSingleFlight();
    }
  } catch (error) {
    if (isAbortError(error)) {
      console.debug("[DEBUG] getAuthHeaders getSession aborted", error);
    } else {
      console.error("[DEBUG] getAuthHeaders getSession failed", error);
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // Provenance: the backend stamps created_via from this (web vs desktop).
    "X-Client-Platform": /Electron/i.test(navigator.userAgent)
      ? "desktop"
      : "web",
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  } else {
    // Fall back to cached token only when no active session is available.
    const cachedToken = getCachedAccessToken();
    if (cachedToken) {
      headers["Authorization"] = `Bearer ${cachedToken}`;
    }
  }

  debugThrottled(
    "authHeaders:session",
    1500,
    "[DEBUG] getAuthHeaders session",
    {
      hasSession: !!session,
      userId: session?.user?.id,
      expiresAt: session?.expires_at,
      hasAuthHeader: Boolean(headers.Authorization),
    }
  );

  return headers;
};

// Helper function to handle API errors
export const handleApiError = async (response: Response): Promise<void> => {
  console.debug("[DEBUG] API error handler", {
    status: response.status,
    url: response.url,
  });
  if (response.status === 401) {
    // Do not hard redirect here; ProtectedRoute handles navigation based on auth state.
    const authError = new Error("Authentication required");
    authError.name = "AuthRequiredError";
    throw authError;
  } else if (response.status === 403) {
    // Token expired - try to refresh
    console.warn("Token expired or invalid");
    const {
      data: { session },
      error,
    } = await supabase.auth.refreshSession();
    if (error || !session) {
      throw new Error("Session expired. Please log in again.");
    }
    throw new Error("Token expired. Please retry your request.");
  } else if (response.status === 429) {
    throw new Error("Too many requests. Please try again later.");
  }
};

export const isAuthTransitionError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  if ((error as Error).name === "AbortError") return true;
  if ((error as Error).name === "AuthRequiredError") return true;
  const message = String((error as Error).message || "").toLowerCase();
  return (
    message.includes("authentication required") ||
    message.includes("aborterror") ||
    message.includes("operation was aborted")
  );
};

// Helper function to convert ISO date strings to Date objects and snake_case to camelCase
const convertDates = (obj: any): any => {
  // If it's an array, convert each item
  if (Array.isArray(obj)) {
    return obj.map((item) => convertDates(item));
  }

  const result = { ...obj };

  // Convert snake_case boolean fields to camelCase
  // For expenses and income: is_paid -> isPaid
  if (result.is_paid !== undefined && result.isPaid === undefined) {
    result.isPaid = result.is_paid;
    console.log(
      `[convertDates] Converted is_paid (${result.is_paid}) to isPaid`
    );
  }

  // For expenses: is_recurring -> isRecurring
  if (result.is_recurring !== undefined && result.isRecurring === undefined) {
    result.isRecurring = result.is_recurring;
  }

  // For expenses: is_skipped -> isSkipped
  if (result.is_skipped !== undefined && result.isSkipped === undefined) {
    result.isSkipped = result.is_skipped;
  }

  // For expenses: linked_debt_id -> linkedDebtId
  if (
    result.linked_debt_id !== undefined &&
    result.linkedDebtId === undefined
  ) {
    result.linkedDebtId = result.linked_debt_id;
  }

  if (
    result.use_linked_debt_amount !== undefined &&
    result.useLinkedDebtAmount === undefined
  ) {
    result.useLinkedDebtAmount = result.use_linked_debt_amount;
  }

  // For expenses: suspension_period_value -> suspensionPeriodValue
  if (
    result.suspension_period_value !== undefined &&
    result.suspensionPeriodValue === undefined
  ) {
    result.suspensionPeriodValue = result.suspension_period_value;
  }

  // For expenses: suspension_period_unit -> suspensionPeriodUnit
  if (
    result.suspension_period_unit !== undefined &&
    result.suspensionPeriodUnit === undefined
  ) {
    result.suspensionPeriodUnit = result.suspension_period_unit;
  }

  // For expenses: bill_or_budget -> billOrBudget
  if (
    result.bill_or_budget !== undefined &&
    result.billOrBudget === undefined
  ) {
    result.billOrBudget = result.bill_or_budget;
    console.log(
      `[convertDates] Converted bill_or_budget (${result.bill_or_budget}) to billOrBudget`
    );
  }

  // For expenses: linked_shopping_list_id -> linkedShoppingListId
  if (
    result.linked_shopping_list_id !== undefined &&
    result.linkedShoppingListId === undefined
  ) {
    result.linkedShoppingListId = result.linked_shopping_list_id;
  }

  // For expenses: calculation_type -> calculationType
  if (
    result.calculation_type !== undefined &&
    result.calculationType === undefined
  ) {
    result.calculationType = result.calculation_type;
  }

  // For expenses: percent_of_income -> percentOfIncome
  if (
    result.percent_of_income !== undefined &&
    result.percentOfIncome === undefined
  ) {
    result.percentOfIncome = result.percent_of_income;
  }

  // For expenses: income_basis -> incomeBasis
  if (result.income_basis !== undefined && result.incomeBasis === undefined) {
    result.incomeBasis = result.income_basis;
  }

  // For expenses: tax_profile_id -> taxProfileId
  if (
    result.tax_profile_id !== undefined &&
    result.taxProfileId === undefined
  ) {
    result.taxProfileId = result.tax_profile_id;
  }

  // For expenses: tax_profile_name -> taxProfileName
  if (
    result.tax_profile_name !== undefined &&
    result.taxProfileName === undefined
  ) {
    result.taxProfileName = result.tax_profile_name;
  }

  // For expenses: expenses_profile_id -> expensesProfileId
  if (
    result.expenses_profile_id !== undefined &&
    result.expensesProfileId === undefined
  ) {
    result.expensesProfileId = result.expenses_profile_id;
  }

  // For expenses: invoiced_date -> invoicedDate (string)
  if (result.invoiced_date !== undefined && result.invoicedDate === undefined) {
    result.invoicedDate = result.invoiced_date;
    console.log(
      `[convertDates] Converted invoiced_date (${result.invoiced_date}) to invoicedDate`
    );
  }

  // For income: paid_to_date -> paidToDate
  if (result.paid_to_date !== undefined && result.paidToDate === undefined) {
    result.paidToDate = result.paid_to_date;
  }

  // For income: invoice_total -> invoiceTotal
  if (result.invoice_total !== undefined && result.invoiceTotal === undefined) {
    result.invoiceTotal = result.invoice_total;
  }

  // For income: expected_by_date -> expectedByDate (string)
  if (
    result.expected_by_date !== undefined &&
    result.expectedByDate === undefined
  ) {
    result.expectedByDate = result.expected_by_date;
  }

  // For income: received_date -> receivedDate
  if (result.received_date !== undefined && result.receivedDate === undefined) {
    result.receivedDate = result.received_date;
  }

  // For income: deposit_date -> depositDate
  if (result.deposit_date !== undefined && result.depositDate === undefined) {
    result.depositDate = result.deposit_date;
  }

  // For income: depends_on_completion -> dependsOnCompletion
  if (
    result.depends_on_completion !== undefined &&
    result.dependsOnCompletion === undefined
  ) {
    result.dependsOnCompletion = result.depends_on_completion;
  }

  // For income: payment_processor_fee -> paymentProcessorFee
  if (
    result.payment_processor_fee !== undefined &&
    result.paymentProcessorFee === undefined
  ) {
    result.paymentProcessorFee = result.payment_processor_fee;
  }

  // For income: loan_repayment -> loanRepayment
  if (
    result.loan_repayment !== undefined &&
    result.loanRepayment === undefined
  ) {
    result.loanRepayment = result.loan_repayment;
  }

  // For expenses: due_date -> dueDate (string)
  if (result.due_date !== undefined && result.dueDate === undefined) {
    result.dueDate = result.due_date;
  }

  // Convert invoicedDate string to Date object for expenses
  if (result.invoicedDate && typeof result.invoicedDate === "string") {
    try {
      result.invoicedDate = new Date(result.invoicedDate);
      console.log(
        `[convertDates] Converted invoicedDate: ${result.invoicedDate}`
      );
    } catch (error) {
      console.error("Error converting invoicedDate:", error);
    }
  }

  // Convert date strings to Date objects for expenses
  if (result.dueDate && typeof result.dueDate === "string") {
    try {
      result.dueDate = new Date(result.dueDate);
      console.log(`[convertDates] Converted dueDate: ${result.dueDate}`);
    } catch (error) {
      console.error("Error converting dueDate:", error);
    }
  }

  if (result.createdAt && typeof result.createdAt === "string") {
    try {
      result.createdAt = new Date(result.createdAt);
    } catch (error) {
      console.error("Error converting createdAt:", error);
    }
  }

  if (result.updatedAt && typeof result.updatedAt === "string") {
    try {
      result.updatedAt = new Date(result.updatedAt);
    } catch (error) {
      console.error("Error converting updatedAt:", error);
    }
  }

  // Convert date strings to Date objects for income
  if (result.expectedByDate && typeof result.expectedByDate === "string") {
    try {
      result.expectedByDate = new Date(result.expectedByDate);
      console.log(
        `[convertDates] Converted expectedByDate: ${result.expectedByDate}`
      );
    } catch (error) {
      console.error("Error converting expectedByDate:", error);
    }
  }

  // Convert receivedDate string to Date object
  if (result.receivedDate && typeof result.receivedDate === "string") {
    try {
      result.receivedDate = new Date(result.receivedDate);
    } catch (error) {
      console.error("Error converting receivedDate:", error);
    }
  }

  // Convert depositDate string to Date object
  if (result.depositDate && typeof result.depositDate === "string") {
    try {
      result.depositDate = new Date(result.depositDate);
    } catch (error) {
      console.error("Error converting depositDate:", error);
    }
  }

  // Convert numeric income fields
  if (
    result.paymentProcessorFee !== undefined &&
    typeof result.paymentProcessorFee === "string"
  ) {
    result.paymentProcessorFee = parseFloat(result.paymentProcessorFee) || 0;
  }
  if (
    result.loanRepayment !== undefined &&
    typeof result.loanRepayment === "string"
  ) {
    result.loanRepayment = parseFloat(result.loanRepayment) || 0;
  }

  return result;
};

// Migration utility
export const migrateLocalStorage = async (): Promise<void> => {
  try {
    // Get all data from localStorage
    const localStorage = {
      expenses: window.localStorage.getItem("finance_expenses"),
      income: window.localStorage.getItem("finance_income"),
      debts: window.localStorage.getItem("finance_debts"),
    };

    // Only migrate if there's data
    if (localStorage.expenses || localStorage.income || localStorage.debts) {
      const headers = await getAuthHeaders();
      console.log("[migration] Sending localStorage data to /api/migrate", {
        hasExpenses: Boolean(localStorage.expenses),
        hasIncome: Boolean(localStorage.income),
        hasDebts: Boolean(localStorage.debts),
      });

      const response = await fetch(`${API_URL}/migrate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({ localStorage }),
      });

      if (!response.ok) {
        throw new Error("Failed to migrate data");
      }

      console.log("[migration] Data migration completed successfully");
    }
  } catch (error) {
    console.error("[migration] Error:", error);
    throw error;
  }
};

// Expense API
export const expenseAPI = {
  getAll: async (): Promise<Expense[]> => {
    // Return demo data if in demo mode
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getExpenses());
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses`, { headers });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch expenses");
      }

      const expenses = await response.json();
      console.log("[expenseAPI.getAll] Response:", {
        count: Array.isArray(expenses) ? expenses.length : 0,
        sample: Array.isArray(expenses) ? expenses[0] : expenses,
      });
      return expenses.map(convertDates);
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] expenseAPI.getAll aborted", error);
        return [];
      }
      console.error("Error fetching expenses:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Expense | null> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getExpenseById(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/${id}`, { headers });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch expense");
      }

      const expense = await response.json();
      return convertDates(expense);
    } catch (error) {
      console.error(`Error fetching expense with id ${id}:`, error);
      throw error;
    }
  },

  add: async (expense: Omit<Expense, "id">): Promise<Expense> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.addExpense(expense));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses`, {
        method: "POST",
        headers,
        body: JSON.stringify(expense),
      });

      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json().catch(() => ({}));
          const duplicateId = errorData?.duplicateExpense?.id;
          const message = duplicateId
            ? `${errorData?.error || "Duplicate expense detected."} Existing ID: ${duplicateId}`
            : errorData?.error || "Duplicate expense detected.";
          throw new DuplicateExpenseApiError(
            message,
            errorData?.duplicateExpense
          );
        }
        await handleApiError(response);
        throw new Error("Failed to add expense");
      }

      const newExpense = await response.json();
      console.log("[expenseAPI.add] Response:", newExpense);
      return convertDates(newExpense);
    } catch (error) {
      console.error("Error adding expense:", error);
      throw error;
    }
  },

  update: async (expense: Expense): Promise<Expense> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.updateExpense(expense));
    }
    try {
      console.log("[expenseAPI.update] Request payload:", {
        id: expense.id,
        isSkipped: expense.isSkipped,
      });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/${expense.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(expense),
      });

      if (!response.ok) {
        if (response.status === 409) {
          const errorData = await response.json().catch(() => ({}));
          const duplicateId = errorData?.duplicateExpense?.id;
          const message = duplicateId
            ? `${errorData?.error || "Duplicate expense detected."} Existing ID: ${duplicateId}`
            : errorData?.error || "Duplicate expense detected.";
          throw new DuplicateExpenseApiError(
            message,
            errorData?.duplicateExpense
          );
        }
        await handleApiError(response);
        throw new Error("Failed to update expense");
      }

      const updatedExpense = await response.json();
      console.log("[expenseAPI.update] Raw response:", {
        id: updatedExpense.id,
        is_skipped: updatedExpense.is_skipped,
        isSkipped: updatedExpense.isSkipped,
      });
      const converted = convertDates(updatedExpense);
      console.log("[expenseAPI.update] After convertDates:", {
        id: converted.id,
        isSkipped: converted.isSkipped,
      });
      return converted;
    } catch (error) {
      console.error("Error updating expense:", error);
      throw error;
    }
  },

  regenerateInstances: async (
    expenseId: string,
    monthsAhead: number = 24
  ): Promise<{ instancesCreated: number; instances: Expense[] }> => {
    if (demoService.isDemo()) {
      return Promise.resolve({ instancesCreated: 0, instances: [] });
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/expenses/${expenseId}/regenerate-instances`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ monthsAhead }),
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to regenerate recurring instances");
    }
    const data = await response.json();
    return {
      instancesCreated: Number(data.instancesCreated || 0),
      instances: Array.isArray(data.instances)
        ? data.instances.map(convertDates)
        : [],
    };
  },

  delete: async (id: string): Promise<void> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.deleteExpense(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete expense");
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
      throw error;
    }
  },

  linkAsRecurring: async (
    expenseIds: string[],
    seriesRootId: string
  ): Promise<{ updated: number }> => {
    if (demoService.isDemo()) {
      return Promise.resolve({ updated: expenseIds.length });
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/link-as-recurring`, {
        method: "POST",
        headers,
        body: JSON.stringify({ expenseIds, seriesRootId }),
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to link expenses as recurring");
      }
      return response.json();
    } catch (error) {
      console.error("Error linking expenses as recurring:", error);
      throw error;
    }
  },

  merge: async (payload: {
    survivorId: string;
    sourceIds: string[];
    newDate: string;
    makeRecurring: boolean;
    recurrencePattern: string | null;
    monthsAhead?: number;
  }): Promise<{
    survivor: Expense;
    deletedIds: string[];
    instances: Expense[];
  }> => {
    if (demoService.isDemo()) {
      return Promise.resolve({
        survivor: {} as Expense,
        deletedIds: [],
        instances: [],
      });
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/merge`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to merge expenses");
      }
      const data = await response.json();
      return {
        survivor: convertDates(data.survivor),
        deletedIds: Array.isArray(data.deletedIds) ? data.deletedIds : [],
        instances: Array.isArray(data.instances)
          ? data.instances.map(convertDates)
          : [],
      };
    } catch (error) {
      console.error("Error merging expenses:", error);
      throw error;
    }
  },

  togglePaid: async (id: string): Promise<Expense> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.toggleExpensePaid(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/${id}/toggle-paid`, {
        method: "PATCH",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to toggle expense paid status");
      }

      const expense = await response.json();
      return convertDates(expense);
    } catch (error) {
      console.error("Error toggling expense paid status:", error);
      throw error;
    }
  },
  importMany: async (expenses: Omit<Expense, "id">[]): Promise<Expense[]> => {
    if (demoService.isDemo()) {
      return Promise.resolve(expenses.map((expense) => expense as Expense));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/expenses/import`, {
        method: "POST",
        headers,
        body: JSON.stringify(expenses),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Expense import failed response:",
          response.status,
          errorText
        );
        await handleApiError(response);
        throw new Error("Failed to import expenses");
      }

      const importedExpenses = await response.json();
      return importedExpenses.map(convertDates);
    } catch (error) {
      console.error("Error importing expenses:", error);
      throw error;
    }
  },
};

export type ExpensePayment = {
  id: string;
  expense_id: string;
  user_id: string;
  amount: number;
  payment_date: string;
  payment_method: string | null;
  notes: string | null;
  linked_transaction_id: string | null;
  created_at: string;
  updated_at: string;
};

export type PaymentStatus = {
  totalPaid: number;
  isPaid: boolean;
  amount: number;
};

export type PaymentSuggestion = {
  transaction: {
    transaction_id: string;
    name?: string;
    merchant_name?: string;
    amount: number;
    date: string;
    [key: string]: unknown;
  };
  score: number;
  daysOff: number;
  amountDiff: number;
};

export const expensePaymentAPI = {
  list: async (expenseId: string): Promise<ExpensePayment[]> => {
    if (demoService.isDemo()) return [];
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/expenses/${expenseId}/payments`, {
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to load payments");
    }
    return response.json();
  },

  create: async (
    expenseId: string,
    payload: {
      amount: number;
      paymentDate: string;
      paymentMethod?: string | null;
      notes?: string | null;
      linkedTransactionId?: string | null;
    }
  ): Promise<{ payment: ExpensePayment; status: PaymentStatus }> => {
    if (demoService.isDemo()) {
      return Promise.resolve({
        payment: {
          id: `demo-${Date.now()}`,
          expense_id: expenseId,
          user_id: "demo",
          amount: payload.amount,
          payment_date: payload.paymentDate,
          payment_method: payload.paymentMethod || null,
          notes: payload.notes || null,
          linked_transaction_id: payload.linkedTransactionId || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        status: { totalPaid: payload.amount, isPaid: false, amount: 0 },
      });
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/expenses/${expenseId}/payments`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to create payment");
    }
    return response.json();
  },

  update: async (
    paymentId: string,
    payload: Partial<{
      amount: number;
      paymentDate: string;
      paymentMethod: string | null;
      notes: string | null;
      linkedTransactionId: string | null;
    }>
  ): Promise<{ payment: ExpensePayment; status: PaymentStatus }> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/expenses/payments/${paymentId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to update payment");
    }
    return response.json();
  },

  remove: async (
    paymentId: string
  ): Promise<{ deleted: true; expenseId: string; status: PaymentStatus }> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/expenses/payments/${paymentId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to delete payment");
    }
    return response.json();
  },

  suggestions: async (
    expenseId: string,
    opts?: {
      paymentDate?: string;
      q?: string;
      windowDays?: number;
      limit?: number;
    }
  ): Promise<{ suggestions: PaymentSuggestion[] }> => {
    if (demoService.isDemo()) return { suggestions: [] };
    const params = new URLSearchParams();
    if (opts?.paymentDate) params.set("paymentDate", opts.paymentDate);
    if (opts?.q) params.set("q", opts.q);
    if (opts?.windowDays) params.set("windowDays", String(opts.windowDays));
    if (opts?.limit) params.set("limit", String(opts.limit));
    const headers = await getAuthHeaders();
    const url = `${API_URL}/expenses/${expenseId}/payment-suggestions${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await fetch(url, { headers });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to load payment suggestions");
    }
    return response.json();
  },
};

// Comparison Items API
export type ComparisonItemData = {
  id?: string;
  label: string;
  price: number;
  matchers: { name: string; vendor?: string }[];
  sortOrder?: number;
};

export const comparisonItemsAPI = {
  getAll: async (): Promise<ComparisonItemData[]> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/comparison-items`, { headers });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch comparison items");
      }
      const items = await response.json();
      console.log("[comparisonItemsAPI.getAll] Response:", {
        count: items.length,
      });
      return items;
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] comparisonItemsAPI.getAll aborted", error);
        return [];
      }
      console.error("Error fetching comparison items:", error);
      throw error;
    }
  },

  saveAll: async (
    items: ComparisonItemData[]
  ): Promise<ComparisonItemData[]> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/comparison-items`, {
        method: "PUT",
        headers,
        body: JSON.stringify(items),
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to save comparison items");
      }
      const saved = await response.json();
      console.log("[comparisonItemsAPI.saveAll] Response:", {
        count: saved.length,
      });
      return saved;
    } catch (error) {
      console.error("Error saving comparison items:", error);
      throw error;
    }
  },
};

// Income API
export const incomeAPI = {
  getAll: async (): Promise<Income[]> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getIncome());
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income`, { headers });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch income");
      }

      const income = await response.json();
      return income.map(convertDates);
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] incomeAPI.getAll aborted", error);
        return [];
      }
      console.error("Error fetching income:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Income | null> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getIncomeById(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income/${id}`, { headers });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch income");
      }

      const income = await response.json();
      return convertDates(income);
    } catch (error) {
      console.error(`Error fetching income with id ${id}:`, error);
      throw error;
    }
  },

  add: async (income: Omit<Income, "id">): Promise<Income> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.addIncome(income));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income`, {
        method: "POST",
        headers,
        body: JSON.stringify(income),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to add income");
      }

      const newIncome = await response.json();
      return convertDates(newIncome);
    } catch (error) {
      console.error("Error adding income:", error);
      throw error;
    }
  },

  update: async (income: Income): Promise<Income> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.updateIncome(income));
    }
    try {
      console.log("[incomeAPI.update] Request:", {
        id: income.id,
        payload: income,
      });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income/${income.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(income),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[incomeAPI.update] Error response:", {
          status: response.status,
          body: errorText,
        });
        await handleApiError(response);
        throw new Error("Failed to update income");
      }

      const updatedIncome = await response.json();
      return convertDates(updatedIncome);
    } catch (error) {
      console.error("Error updating income:", error);
      throw error;
    }
  },

  // Returns the deletion-log id so the caller can offer an undo, and the date
  // the undo window closes for records mirrored from Account Center.
  delete: async (
    id: string
  ): Promise<{
    deletedRecordId?: string | null;
    restorableUntil?: string | null;
  }> => {
    if (demoService.isDemo()) {
      await demoAPI.deleteIncome(id);
      return {};
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income/${id}`, {
        method: "DELETE",
        headers,
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        // The server refuses rather than deleting only the local copy when the
        // upstream record cannot be removed, so surface its reason verbatim.
        if (data?.error) throw new Error(data.error);
        await handleApiError(response);
        throw new Error("Failed to delete income");
      }
      return {
        deletedRecordId: data?.deletedRecordId ?? null,
        restorableUntil: data?.restorableUntil ?? null,
      };
    } catch (error) {
      console.error("Error deleting income:", error);
      throw error;
    }
  },

  restoreDeleted: async (deletedRecordId: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/deleted-records/${deletedRecordId}/restore`,
      { method: "POST", headers }
    );
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error || "Failed to restore income");
    }
  },

  skipOccurrence: async (incomeId: string, skipDate: string): Promise<void> => {
    if (demoService.isDemo()) {
      return Promise.resolve();
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income/${incomeId}/skip`, {
        method: "POST",
        headers,
        body: JSON.stringify({ skipDate }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[incomeAPI.skipOccurrence] Error response:", {
          status: response.status,
          body: errorText,
        });
        await handleApiError(response);
        throw new Error("Failed to skip income occurrence");
      }
    } catch (error) {
      console.error("Error skipping income occurrence:", error);
      throw error;
    }
  },

  deleteSeries: async (
    incomeId: string,
    deletePast: boolean
  ): Promise<void> => {
    if (demoService.isDemo()) {
      return Promise.resolve();
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/income/${incomeId}/series?deletePast=${deletePast}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[incomeAPI.deleteSeries] Error response:", {
          status: response.status,
          body: errorText,
        });
        await handleApiError(response);
        throw new Error("Failed to delete income series");
      }
    } catch (error) {
      console.error("Error deleting income series:", error);
      throw error;
    }
  },

  togglePaid: async (id: string): Promise<Income> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.toggleIncomePaid(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/income/${id}/toggle-paid`, {
        method: "PATCH",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to toggle income paid status");
      }

      const income = await response.json();
      return convertDates(income);
    } catch (error) {
      console.error("Error toggling income paid status:", error);
      throw error;
    }
  },
};

// Debt API
export const debtAPI = {
  // Linked-account transactions recorded as payments on debts.
  getPayments: async (): Promise<DebtPayment[]> => {
    if (demoService.isDemo()) return [];
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/debt-payments`, { headers });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch debt payments");
    }
    return await response.json();
  },

  getAll: async (): Promise<Debt[]> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getDebts());
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts`, { headers });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch debts");
      }

      return await response.json();
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] debtAPI.getAll aborted", error);
        return [];
      }
      console.error("Error fetching debts:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Debt | null> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getDebtById(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts/${id}`, { headers });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch debt");
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching debt with id ${id}:`, error);
      throw error;
    }
  },

  add: async (debt: Omit<Debt, "id">): Promise<Debt> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.addDebt(debt));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts`, {
        method: "POST",
        headers,
        body: JSON.stringify(debt),
      });

      if (!response.ok) {
        let errorBody: unknown = null;
        try {
          errorBody = await response.json();
        } catch (parseError) {
          try {
            errorBody = await response.text();
          } catch (textError) {
            errorBody = null;
          }
        }
        console.error("[debtAPI.add] Failed response:", {
          status: response.status,
          statusText: response.statusText,
          errorBody,
          payload: debt,
        });
        await handleApiError(response);
        throw new Error("Failed to add debt");
      }

      const newDebt = await response.json();
      console.log("[debtAPI.add] Response:", newDebt);
      return newDebt;
    } catch (error) {
      console.error("Error adding debt:", error);
      throw error;
    }
  },

  update: async (debt: Debt): Promise<Debt> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.updateDebt(debt));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts/${debt.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(debt),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update debt");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating debt:", error);
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.deleteDebt(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete debt");
      }
    } catch (error) {
      console.error("Error deleting debt:", error);
      throw error;
    }
  },

  importInitialData: async (debts: Omit<Debt, "id">[]): Promise<Debt[]> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debts/import`, {
        method: "POST",
        headers,
        body: JSON.stringify(debts),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to import debts");
      }

      return await response.json();
    } catch (error) {
      console.error("Error importing debts:", error);
      throw error;
    }
  },
};

// Debt Share API
export interface DebtShare {
  id: string;
  debtId?: string;
  creditor?: string;
  expiresAt?: string;
  createdAt: string;
  requiresPassword: boolean;
}

export interface DebtShareValidationResult {
  valid: boolean;
  debts: Debt[];
  creditor?: string;
  expiresAt?: string;
}

export const debtShareAPI = {
  // Create a new share link
  create: async (params: {
    debtId?: string;
    creditor?: string;
    password: string;
    expiresAt?: string | null;
  }): Promise<{ shareId: string }> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debt-shares`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to create debt share");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating debt share:", error);
      throw error;
    }
  },

  // Get share info (public - no auth needed)
  getInfo: async (shareId: string): Promise<DebtShare> => {
    try {
      const response = await fetch(`${API_URL}/debt-shares/${shareId}`);

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Share not found");
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting debt share info:", error);
      throw error;
    }
  },

  // Validate password and get debts (public - no auth needed)
  validate: async (
    shareId: string,
    password: string
  ): Promise<DebtShareValidationResult> => {
    try {
      const response = await fetch(
        `${API_URL}/debt-shares/${shareId}/validate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password }),
        }
      );

      if (response.status === 401) {
        throw new Error("Invalid password");
      }

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Failed to validate share");
      }

      return await response.json();
    } catch (error) {
      console.error("Error validating debt share:", error);
      throw error;
    }
  },

  // Delete a share link
  delete: async (shareId: string): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/debt-shares/${shareId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete debt share");
      }
    } catch (error) {
      console.error("Error deleting debt share:", error);
      throw error;
    }
  },

  // List all shares for a debt
  listForDebt: async (params: {
    debtId?: string;
    creditor?: string;
  }): Promise<
    Array<{
      share_id: string;
      created_at: string;
      expires_at: string | null;
    }>
  > => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (params.debtId) queryParams.set("debtId", params.debtId);
      if (params.creditor) queryParams.set("creditor", params.creditor);

      const response = await fetch(
        `${API_URL}/debt-shares?${queryParams.toString()}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to list debt shares");
      }

      return await response.json();
    } catch (error) {
      console.error("Error listing debt shares:", error);
      throw error;
    }
  },
};

// Helper function to convert goal dates
const convertGoalDates = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertGoalDates(item));
  }

  const result = { ...obj };

  if (result.targetDate && typeof result.targetDate === "string") {
    result.targetDate = new Date(result.targetDate);
  }
  if (result.createdAt && typeof result.createdAt === "string") {
    result.createdAt = new Date(result.createdAt);
  }
  if (result.updatedAt && typeof result.updatedAt === "string") {
    result.updatedAt = new Date(result.updatedAt);
  }

  return result;
};

// Goal API
export const goalAPI = {
  getAll: async (): Promise<Goal[]> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getGoals());
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals`, { headers });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch goals");
      }

      const goals = await response.json();
      return goals.map(convertGoalDates);
    } catch (error) {
      console.error("Error fetching goals:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Goal | null> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.getGoalById(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals/${id}`, { headers });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch goal");
      }

      const goal = await response.json();
      return convertGoalDates(goal);
    } catch (error) {
      console.error(`Error fetching goal with id ${id}:`, error);
      throw error;
    }
  },

  add: async (
    goal: Omit<Goal, "id" | "createdAt" | "updatedAt">
  ): Promise<Goal> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.addGoal(goal));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals`, {
        method: "POST",
        headers,
        body: JSON.stringify(goal),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to add goal");
      }

      const newGoal = await response.json();
      return convertGoalDates(newGoal);
    } catch (error) {
      console.error("Error adding goal:", error);
      throw error;
    }
  },

  update: async (goal: Goal): Promise<Goal> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.updateGoal(goal));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals/${goal.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(goal),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update goal");
      }

      const updatedGoal = await response.json();
      return convertGoalDates(updatedGoal);
    } catch (error) {
      console.error("Error updating goal:", error);
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    if (demoService.isDemo()) {
      return Promise.resolve(demoAPI.deleteGoal(id));
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete goal");
      }
    } catch (error) {
      console.error("Error deleting goal:", error);
      throw error;
    }
  },

  updateLinkedAccounts: async (
    goalId: string,
    accountIds: string[]
  ): Promise<Goal> => {
    if (demoService.isDemo()) {
      return Promise.resolve(
        demoAPI.updateGoalLinkedAccounts(goalId, accountIds)
      );
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/goals/${goalId}/accounts`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ accountIds }),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update goal linked accounts");
      }

      const updatedGoal = await response.json();
      return convertGoalDates(updatedGoal);
    } catch (error) {
      console.error("Error updating goal linked accounts:", error);
      throw error;
    }
  },
};

// Dashboard types
export interface DashboardSection {
  id: string;
  title?: string;
  label?: string;
  cards: DashboardCard[];
}

export interface DashboardCard {
  id: string;
  type?: string;
  label?: string;
}

export interface Dashboard {
  id: string;
  userId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  layout: DashboardSection[];
  cardColors: Record<string, string>;
  sectionColumns: Record<string, number>;
  coloringEnabled: boolean;
  visibility: {
    hiddenGroups: Record<string, boolean>;
    hiddenCards: Record<string, boolean>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DashboardShare {
  id: string;
  dashboardId: string;
  dashboardName?: string;
  dashboardDescription?: string;
  expiresAt?: string;
  isLive: boolean;
  createdAt: string;
  requiresPassword: boolean;
}

export interface DashboardShareValidationResult {
  valid: boolean;
  dashboard: Dashboard;
  financialData: {
    debts: Debt[];
    expenses: Expense[];
    income: Income[];
    goals: Goal[];
  };
  isLive: boolean;
  expiresAt?: string;
}

export interface PageShareInfo {
  id: string;
  pageType:
    | "expenses"
    | "income"
    | "debts"
    | "shopping"
    | "forecasting"
    | "goals"
    | "transactions"
    | "tax"
    | "mindmap"
    | "housing"
    | "mortgage"
    | "wife";
  expiresAt?: string | null;
  createdAt?: string;
  isPublic: boolean;
  requiresAuth: boolean;
  requiresPassword: boolean;
  label?: string | null;
  payload?: any;
}

export type PageShareType = PageShareInfo["pageType"];

export interface PageShareValidationResult {
  valid: boolean;
  pageType: PageShareType;
  expenses?: Expense[];
  income?: Income[];
  debts?: Debt[];
  shoppingLists?: ShoppingList[];
  payload?: any;
  label?: string | null;
  isLive: boolean;
  expiresAt?: string | null;
}

// Dashboard API
export const dashboardAPI = {
  getAll: async (): Promise<Dashboard[]> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - returning empty array");
      return Promise.resolve([]);
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards`, { headers });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch dashboards");
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching dashboards:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<Dashboard | null> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - returning null");
      return Promise.resolve(null);
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards/${id}`, { headers });

      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch dashboard");
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching dashboard with id ${id}:`, error);
      throw error;
    }
  },

  create: async (
    dashboard: Omit<Dashboard, "id" | "userId" | "createdAt" | "updatedAt">
  ): Promise<Dashboard> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - returning mock dashboard");
      return Promise.resolve({
        id: `demo-${Date.now()}`,
        userId: "demo-user",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...dashboard,
      } as Dashboard);
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards`, {
        method: "POST",
        headers,
        body: JSON.stringify(dashboard),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to create dashboard");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating dashboard:", error);
      throw error;
    }
  },

  update: async (
    id: string,
    dashboard: Partial<Dashboard>
  ): Promise<Dashboard> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - returning mock update");
      return Promise.resolve({
        ...dashboard,
        id,
        updatedAt: new Date().toISOString(),
      } as Dashboard);
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(dashboard),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update dashboard");
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating dashboard:", error);
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - mock delete");
      return Promise.resolve();
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards/${id}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        await handleApiError(response);
        throw new Error(errorData.error || "Failed to delete dashboard");
      }
    } catch (error) {
      console.error("Error deleting dashboard:", error);
      throw error;
    }
  },

  setDefault: async (id: string): Promise<Dashboard> => {
    if (demoService.isDemo()) {
      console.debug("[dashboardAPI] Demo mode - mock set default");
      return Promise.resolve({
        id,
        isDefault: true,
      } as Dashboard);
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboards/${id}/set-default`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to set default dashboard");
      }

      return await response.json();
    } catch (error) {
      console.error("Error setting default dashboard:", error);
      throw error;
    }
  },
};

// Dashboard Share API
export const dashboardShareAPI = {
  // Create a new share link
  create: async (params: {
    dashboardId: string;
    password: string;
    expiresAt?: string | null;
    isLive?: boolean;
    snapshotData?: object;
  }): Promise<{ shareId: string }> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboard-shares`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to create dashboard share");
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating dashboard share:", error);
      throw error;
    }
  },

  // Get share info (public - no auth needed)
  getInfo: async (shareId: string): Promise<DashboardShare> => {
    try {
      const response = await fetch(`${API_URL}/dashboard-shares/${shareId}`);

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Share not found");
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting dashboard share info:", error);
      throw error;
    }
  },

  // Validate password and get dashboard data (public - no auth needed)
  validate: async (
    shareId: string,
    password: string
  ): Promise<DashboardShareValidationResult> => {
    try {
      const response = await fetch(
        `${API_URL}/dashboard-shares/${shareId}/validate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password }),
        }
      );

      if (response.status === 401) {
        throw new Error("Invalid password");
      }

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Failed to validate share");
      }

      const data = await response.json();
      // Transform snake_case financial data to camelCase
      if (data.financialData) {
        if (data.financialData.expenses) {
          data.financialData.expenses = convertDates(
            data.financialData.expenses
          );
        }
        if (data.financialData.income) {
          data.financialData.income = convertDates(data.financialData.income);
        }
        if (data.financialData.debts) {
          data.financialData.debts = convertDates(data.financialData.debts);
        }
        if (data.financialData.goals) {
          data.financialData.goals = convertDates(data.financialData.goals);
        }
      }
      return data;
    } catch (error) {
      console.error("Error validating dashboard share:", error);
      throw error;
    }
  },

  // Delete a share link
  delete: async (shareId: string): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/dashboard-shares/${shareId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete dashboard share");
      }
    } catch (error) {
      console.error("Error deleting dashboard share:", error);
      throw error;
    }
  },

  // List all shares for a dashboard
  listForDashboard: async (
    dashboardId: string
  ): Promise<
    Array<{
      id: string;
      dashboardId: string;
      expiresAt: string | null;
      isLive: boolean;
      createdAt: string;
    }>
  > => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/dashboard-shares?dashboardId=${dashboardId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to list dashboard shares");
      }

      return await response.json();
    } catch (error) {
      console.error("Error listing dashboard shares:", error);
      throw error;
    }
  },
};

// Page Share API (Expenses/Income)
export const pageShareAPI = {
  create: async (params: {
    pageType: PageShareType;
    password?: string;
    expiresAt?: string | null;
    isPublic: boolean;
    requiresAuth: boolean;
    payload?: any;
    label?: string | null;
  }): Promise<{ shareId: string }> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/page-shares`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        // For non-401/403 errors, try to surface server response body to help debugging.
        const bodyText = await response.text().catch(() => "");
        console.error("[pageShareAPI] create failed", {
          status: response.status,
          url: response.url,
          bodyText: bodyText?.slice(0, 2000),
        });
        await handleApiError(response);
        throw new Error(
          bodyText
            ? `Failed to create page share: ${bodyText}`
            : "Failed to create page share"
        );
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating page share:", error);
      throw error;
    }
  },

  getInfo: async (shareId: string): Promise<PageShareInfo> => {
    try {
      const response = await fetch(`${API_URL}/page-shares/${shareId}`);

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Share not found");
      }

      return await response.json();
    } catch (error) {
      console.error("Error getting page share info:", error);
      throw error;
    }
  },

  validate: async (
    shareId: string,
    password?: string
  ): Promise<PageShareValidationResult> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/page-shares/${shareId}/validate`,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password }),
        }
      );

      if (response.status === 401) {
        throw new Error("Unauthorized");
      }

      if (response.status === 410) {
        throw new Error("Share link has expired");
      }

      if (!response.ok) {
        throw new Error("Failed to validate share");
      }

      const data = await response.json();
      // Transform snake_case to camelCase for financial data arrays
      if (data.expenses) {
        data.expenses = convertDates(data.expenses);
      }
      if (data.income) {
        data.income = convertDates(data.income);
      }
      if (data.debts) {
        data.debts = convertDates(data.debts);
      }
      if (data.shoppingLists) {
        data.shoppingLists = data.shoppingLists.map(convertShoppingList);
      }
      return data;
    } catch (error) {
      console.error("Error validating page share:", error);
      throw error;
    }
  },

  delete: async (shareId: string): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/page-shares/${shareId}`, {
        method: "DELETE",
        headers,
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete page share");
      }
    } catch (error) {
      console.error("Error deleting page share:", error);
      throw error;
    }
  },

  update: async (
    shareId: string,
    params: { label?: string | null; payload?: any }
  ): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/page-shares/${shareId}`, {
        method: "PUT",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update page share");
      }
    } catch (error) {
      console.error("Error updating page share:", error);
      throw error;
    }
  },

  list: async (pageType: PageShareType): Promise<PageShareInfo[]> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/page-shares?pageType=${pageType}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to list page shares");
      }

      return await response.json();
    } catch (error) {
      console.error("Error listing page shares:", error);
      throw error;
    }
  },
};

// Helper to convert shopping list snake_case to camelCase
const convertShoppingList = (raw: any): ShoppingList => {
  const items = (raw.shopping_list_items || []).map((item: any) => ({
    id: item.id,
    listId: item.list_id,
    title: item.title,
    vendor: item.vendor,
    link: item.link,
    price: Number(item.price || 0),
    dateRequested: item.date_requested
      ? new Date(item.date_requested)
      : new Date(),
    dueDate: item.due_date ? new Date(item.due_date) : undefined,
    isPaid: item.is_paid ?? item.isPaid ?? false,
    isSkipped: item.is_skipped ?? item.isSkipped ?? false,
    paymentDate: item.payment_date ? new Date(item.payment_date) : undefined,
    paymentAccount: item.payment_account ?? item.paymentAccount,
    createdAt: new Date(item.created_at),
    updatedAt: new Date(item.updated_at),
  }));
  return {
    id: raw.id,
    title: raw.title,
    status: raw.status,
    dueDateMode: raw.due_date_mode,
    dueDate: raw.due_date ? new Date(raw.due_date) : undefined,
    isRecurring: raw.is_recurring ?? raw.isRecurring ?? false,
    recurrencePattern:
      raw.recurrence_pattern ?? raw.recurrencePattern ?? undefined,
    recurrenceType: raw.recurrence_type ?? raw.recurrenceType ?? undefined,
    recurrenceConfig:
      raw.recurrence_config ?? raw.recurrenceConfig ?? undefined,
    linkedExpenseId: raw.linked_expense_id,
    items,
    createdAt: new Date(raw.created_at),
    updatedAt: new Date(raw.updated_at),
  };
};

const convertShoppingListItem = (raw: any): ShoppingListItem => ({
  id: raw.id,
  listId: raw.list_id,
  title: raw.title,
  vendor: raw.vendor,
  link: raw.link,
  price: Number(raw.price || 0),
  dateRequested: raw.date_requested ? new Date(raw.date_requested) : new Date(),
  dueDate: raw.due_date ? new Date(raw.due_date) : undefined,
  isPaid: raw.is_paid ?? raw.isPaid ?? false,
  isSkipped: raw.is_skipped ?? raw.isSkipped ?? false,
  paymentDate: raw.payment_date ? new Date(raw.payment_date) : undefined,
  paymentAccount: raw.payment_account ?? raw.paymentAccount,
  createdAt: new Date(raw.created_at),
  updatedAt: new Date(raw.updated_at),
});

// Shopping List API
export const shoppingListAPI = {
  getAll: async ({
    includeShared = false,
  }: { includeShared?: boolean } = {}): Promise<ShoppingList[]> => {
    try {
      const headers = await getAuthHeaders();
      const query = new URLSearchParams();
      if (includeShared) {
        query.set("includeShared", "true");
      }
      const url = `${API_URL}/shopping-lists${
        query.toString() ? `?${query.toString()}` : ""
      }`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch shopping lists");
      }
      const lists = await response.json();
      console.debug("[shoppingListAPI.getAll] Fetched", lists.length, "lists");
      return lists.map(convertShoppingList);
    } catch (error) {
      console.error("Error fetching shopping lists:", error);
      throw error;
    }
  },

  getById: async (id: string): Promise<ShoppingList | null> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-lists/${id}`, {
        headers,
      });
      if (response.status === 404) return null;
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to fetch shopping list");
      }
      const list = await response.json();
      return convertShoppingList(list);
    } catch (error) {
      console.error("Error fetching shopping list:", error);
      throw error;
    }
  },

  create: async (list: {
    title: string;
    dueDateMode?: string;
    dueDate?: string;
    isRecurring?: boolean;
    recurrencePattern?: "weekly" | "biweekly" | "monthly";
    recurrenceType?: "daily" | "weekly" | "monthly" | "annually";
    recurrenceConfig?: Record<string, any>;
  }): Promise<ShoppingList> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-lists`, {
        method: "POST",
        headers,
        body: JSON.stringify(list),
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.error("[shoppingListAPI.create] Error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        await handleApiError(response);
        throw new Error(
          `Failed to create shopping list: ${response.status} ${errorText}`
        );
      }
      const created = await response.json();
      console.debug("[shoppingListAPI.create] Created list:", created.id);
      return convertShoppingList(created);
    } catch (error) {
      console.error("Error creating shopping list:", error);
      throw error;
    }
  },

  update: async (
    id: string,
    list: Partial<{
      title: string;
      dueDateMode: string;
      dueDate: string;
      status: string;
      isRecurring: boolean;
      recurrencePattern: "weekly" | "biweekly" | "monthly";
      recurrenceType: "daily" | "weekly" | "monthly" | "annually";
      recurrenceConfig: Record<string, any>;
    }>
  ): Promise<ShoppingList> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-lists/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(list),
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        await handleApiError(response);
        throw new Error(
          `Failed to update shopping list: ${response.status} ${errorText}`
        );
      }
      const updated = await response.json();
      return convertShoppingList(updated);
    } catch (error) {
      console.error("Error updating shopping list:", error);
      throw error;
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-lists/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete shopping list");
      }
    } catch (error) {
      console.error("Error deleting shopping list:", error);
      throw error;
    }
  },

  archive: async (id: string): Promise<ShoppingList> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-lists/${id}/archive`, {
        method: "PATCH",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to archive shopping list");
      }
      const archived = await response.json();
      return convertShoppingList(archived);
    } catch (error) {
      console.error("Error archiving shopping list:", error);
      throw error;
    }
  },

  addItem: async (
    listId: string,
    item: {
      title: string;
      vendor?: string;
      link?: string;
      price: number;
      dueDate?: string;
    }
  ): Promise<ShoppingListItem> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/shopping-lists/${listId}/items`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(item),
        }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to add shopping list item");
      }
      const created = await response.json();
      console.debug("[shoppingListAPI.addItem] Added item:", created.id);
      return convertShoppingListItem(created);
    } catch (error) {
      console.error("Error adding shopping list item:", error);
      throw error;
    }
  },

  updateItem: async (
    itemId: string,
    item: Partial<{
      title: string;
      vendor: string;
      link: string;
      price: number;
      dueDate: string;
      isPaid: boolean;
      isSkipped: boolean;
      paymentDate: string | null;
      paymentAccount: string | null;
    }>
  ): Promise<ShoppingListItem> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-list-items/${itemId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(item),
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to update shopping list item");
      }
      const updated = await response.json();
      return convertShoppingListItem(updated);
    } catch (error) {
      console.error("Error updating shopping list item:", error);
      throw error;
    }
  },

  deleteItem: async (itemId: string): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_URL}/shopping-list-items/${itemId}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to delete shopping list item");
      }
    } catch (error) {
      console.error("Error deleting shopping list item:", error);
      throw error;
    }
  },

  syncExpense: async (
    listId: string
  ): Promise<{ expense: Expense; total: number }> => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_URL}/shopping-lists/${listId}/sync-expense`,
        {
          method: "POST",
          headers,
        }
      );
      if (!response.ok) {
        await handleApiError(response);
        throw new Error("Failed to sync shopping list expense");
      }
      const result = await response.json();
      console.debug(
        "[shoppingListAPI.syncExpense] Synced expense for list:",
        listId,
        "total:",
        result.total
      );
      return {
        ...result,
        expense: result?.expense
          ? convertDates(result.expense)
          : result.expense,
      };
    } catch (error) {
      console.error("Error syncing shopping list expense:", error);
      throw error;
    }
  },
};

const convertIngredient = (ingredient: any): Ingredient => {
  const normalized = convertDates(ingredient);
  return {
    id: normalized.id,
    userId: normalized.user_id ?? normalized.userId ?? null,
    isGlobal: normalized.is_global ?? normalized.isGlobal ?? false,
    name: normalized.name,
    defaultUnit: normalized.default_unit ?? normalized.defaultUnit ?? "item",
    defaultPrice: normalized.default_price ?? normalized.defaultPrice ?? null,
    defaultStore:
      normalized.default_store ?? normalized.defaultStore ?? "Costco",
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertSupply = (supply: any): Supply => {
  const normalized = convertDates(supply);
  return {
    id: normalized.id,
    userId: normalized.user_id ?? normalized.userId ?? null,
    isGlobal: normalized.is_global ?? normalized.isGlobal ?? false,
    name: normalized.name,
    defaultUnit: normalized.default_unit ?? normalized.defaultUnit ?? "item",
    defaultPrice: normalized.default_price ?? normalized.defaultPrice ?? null,
    defaultStore:
      normalized.default_store ?? normalized.defaultStore ?? "Costco",
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertMealTemplateIngredient = (
  ingredient: any
): MealTemplate["ingredients"][number] => {
  const normalized = convertDates(ingredient);
  return {
    id: normalized.id,
    mealTemplateId:
      normalized.meal_template_id ?? normalized.mealTemplateId ?? "",
    ingredientId: normalized.ingredient_id ?? normalized.ingredientId ?? "",
    quantity: Number(normalized.quantity ?? 1),
    unit: normalized.unit ?? "item",
    notes: normalized.notes ?? null,
    ingredient: normalized.ingredient
      ? convertIngredient(normalized.ingredient)
      : undefined,
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertMealTemplateSupply = (
  supply: any
): MealTemplate["supplies"][number] => {
  const normalized = convertDates(supply);
  return {
    id: normalized.id,
    mealTemplateId:
      normalized.meal_template_id ?? normalized.mealTemplateId ?? "",
    supplyId: normalized.supply_id ?? normalized.supplyId ?? "",
    quantity: Number(normalized.quantity ?? 1),
    unit: normalized.unit ?? "item",
    notes: normalized.notes ?? null,
    supply: normalized.supply ? convertSupply(normalized.supply) : undefined,
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertMealTemplate = (mealTemplate: any): MealTemplate => {
  const normalized = convertDates(mealTemplate);
  return {
    id: normalized.id,
    userId: normalized.user_id ?? normalized.userId ?? null,
    isGlobal: normalized.is_global ?? normalized.isGlobal ?? false,
    name: normalized.name,
    description: normalized.description ?? null,
    mealType: normalized.meal_type ?? normalized.mealType ?? "dinner",
    icon: normalized.icon ?? "dinner_dining",
    tags: Array.isArray(normalized.tags) ? normalized.tags : [],
    source: normalized.source ?? null,
    aiGenerated: normalized.ai_generated ?? normalized.aiGenerated ?? false,
    ingredients: Array.isArray(normalized.meal_template_ingredients)
      ? normalized.meal_template_ingredients.map(convertMealTemplateIngredient)
      : Array.isArray(normalized.ingredients)
        ? normalized.ingredients.map(convertMealTemplateIngredient)
        : [],
    supplies: Array.isArray(normalized.meal_template_supplies)
      ? normalized.meal_template_supplies.map(convertMealTemplateSupply)
      : Array.isArray(normalized.supplies)
        ? normalized.supplies.map(convertMealTemplateSupply)
        : [],
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertMealPlanEntry = (entry: any): MealPlanEntry => {
  const normalized = convertDates(entry);
  return {
    id: normalized.id,
    mealPlanId: normalized.meal_plan_id ?? normalized.mealPlanId,
    mealTemplateId: normalized.meal_template_id ?? normalized.mealTemplateId,
    scheduledAt: normalized.scheduled_at ?? normalized.scheduledAt,
    recurrenceType:
      normalized.recurrence_type ?? normalized.recurrenceType ?? "none",
    recurrenceConfig:
      normalized.recurrence_config ?? normalized.recurrenceConfig ?? {},
    notes: normalized.notes ?? null,
    mealTemplate: normalized.meal_template
      ? convertMealTemplate(normalized.meal_template)
      : undefined,
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

const convertMealPlan = (mealPlan: any): MealPlan => {
  const normalized = convertDates(mealPlan);
  return {
    id: normalized.id,
    title: normalized.title,
    startDate: normalized.start_date ?? normalized.startDate,
    endDate: normalized.end_date ?? normalized.endDate,
    monthDescription:
      normalized.month_description ?? normalized.monthDescription ?? null,
    weekDescription:
      normalized.week_description ?? normalized.weekDescription ?? null,
    dayDescription:
      normalized.day_description ?? normalized.dayDescription ?? null,
    entries: Array.isArray(normalized.meal_plan_entries)
      ? normalized.meal_plan_entries.map(convertMealPlanEntry)
      : Array.isArray(normalized.entries)
        ? normalized.entries.map(convertMealPlanEntry)
        : [],
    createdAt: normalized.created_at ?? normalized.createdAt,
    updatedAt: normalized.updated_at ?? normalized.updatedAt,
  };
};

export const mealTemplateAPI = {
  getAll: async (params?: {
    search?: string;
    mealType?: string;
  }): Promise<MealTemplate[]> => {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams();
    if (params?.search) query.set("query", params.search);
    if (params?.mealType) query.set("mealType", params.mealType);
    const response = await fetch(
      `${API_URL}/meal-templates${query.toString() ? `?${query}` : ""}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch meal templates");
    }
    return (await response.json()).map(convertMealTemplate);
  },

  create: async (payload: Record<string, unknown>): Promise<MealTemplate> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-templates`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        mealQuery: payload.query,
        mealType: payload.mealType,
        dietaryHints: payload.dietaryHints,
      }),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to create meal template");
    }
    return convertMealTemplate(await response.json());
  },

  update: async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<MealTemplate> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-templates/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to update meal template");
    }
    return convertMealTemplate(await response.json());
  },

  delete: async (id: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-templates/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to delete meal template");
    }
  },

  generate: async (
    payload: GenerateMealRequest
  ): Promise<GenerateMealResponse> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-templates/generate`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to generate meal template");
    }
    return convertMealTemplate(await response.json());
  },
};

export const ingredientAPI = {
  getAll: async (params?: {
    search?: string;
    store?: string;
  }): Promise<Ingredient[]> => {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams();
    if (params?.search) query.set("query", params.search);
    if (params?.store) query.set("store", params.store);
    const response = await fetch(
      `${API_URL}/ingredients${query.toString() ? `?${query}` : ""}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch ingredients");
    }
    return (await response.json()).map(convertIngredient);
  },
};

export const supplyAPI = {
  getAll: async (params?: {
    search?: string;
    store?: string;
  }): Promise<Supply[]> => {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams();
    if (params?.search) query.set("search", params.search);
    if (params?.store) query.set("store", params.store);
    const response = await fetch(
      `${API_URL}/supplies${query.toString() ? `?${query}` : ""}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch supplies");
    }
    return (await response.json()).map(convertSupply);
  },
};

export const mealPlanAPI = {
  getAll: async (params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<MealPlan[]> => {
    const headers = await getAuthHeaders();
    const query = new URLSearchParams();
    if (params?.startDate) query.set("startDate", params.startDate);
    if (params?.endDate) query.set("endDate", params.endDate);
    const response = await fetch(
      `${API_URL}/meal-plans${query.toString() ? `?${query}` : ""}`,
      { headers }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to fetch meal plans");
    }
    return (await response.json()).map(convertMealPlan);
  },

  create: async (payload: Record<string, unknown>): Promise<MealPlan> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plans`, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to create meal plan");
    }
    return convertMealPlan(await response.json());
  },

  update: async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<MealPlan> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plans/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to update meal plan");
    }
    return convertMealPlan(await response.json());
  },

  delete: async (id: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plans/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to delete meal plan");
    }
  },

  generateSummary: async (
    id: string,
    summaryType: "month" | "week" | "day"
  ): Promise<MealPlan> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plans/${id}/summary`, {
      method: "POST",
      headers,
      body: JSON.stringify({ summaryType }),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to generate meal plan summary");
    }
    return convertMealPlan(await response.json());
  },
};

export const mealPlanEntryAPI = {
  create: async (
    mealPlanId: string,
    payload: Record<string, unknown>
  ): Promise<MealPlanEntry> => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_URL}/meal-plans/${mealPlanId}/entries`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to create meal plan entry");
    }
    return convertMealPlanEntry(await response.json());
  },

  update: async (
    id: string,
    payload: Record<string, unknown>
  ): Promise<MealPlanEntry> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plan-entries/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to update meal plan entry");
    }
    return convertMealPlanEntry(await response.json());
  },

  delete: async (id: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/meal-plan-entries/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      await handleApiError(response);
      throw new Error("Failed to delete meal plan entry");
    }
  },
};

// ---------------------------------------------------------------------------
// External integrations: Focus: Forge and TPC Account Center
// ---------------------------------------------------------------------------
export interface IntegrationStatus {
  provider: string;
  status: string;
  accountLabel?: string;
  account_label?: string;
  baseUrl?: string;
}
export interface ForgeOrganization {
  id: string;
  name: string;
  color?: string;
}
export interface ForgeProject {
  id: string;
  name: string;
  organizationId?: string;
  color?: string;
}
export interface BillingDocument {
  id: string;
  type: "proposal" | "invoice";
  label: string;
  total: number;
  status?: string;
  clientName?: string;
}

export const integrationsAPI = {
  async list(): Promise<IntegrationStatus[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations`, { headers });
    if (!res.ok) throw new Error("Failed to load integrations");
    return res.json();
  },
  async connectForge(token: string): Promise<IntegrationStatus> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/forge`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok)
      throw new Error(data.error || "Failed to connect Focus: Forge");
    return data;
  },
  async connectTpc(token: string): Promise<IntegrationStatus> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/tpc`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok)
      throw new Error(data.error || "Failed to connect TPC Account Center");
    return data;
  },
  async disconnect(provider: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/${provider}`, {
      method: "DELETE",
      headers,
    });
    if (!res.ok) throw new Error("Failed to disconnect");
  },
  async forgeOrganizations(): Promise<ForgeOrganization[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/forge/organizations`, {
      headers,
    });
    if (!res.ok) return [];
    return res.json();
  },
  async forgeProjects(organizationId: string): Promise<ForgeProject[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(
      `${API_URL}/integrations/forge/projects?organizationId=${encodeURIComponent(
        organizationId
      )}`,
      { headers }
    );
    if (!res.ok) return [];
    return res.json();
  },
  async createForgeProject(
    name: string,
    organizationId: string
  ): Promise<ForgeProject> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/forge/projects`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ name, organizationId }),
    });
    const data = await res.json();
    if (!res.ok)
      throw new Error(data.error || "Failed to create Forge project");
    return data;
  },
  async tpcBillingDocuments(): Promise<BillingDocument[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/tpc/billing-documents`, {
      headers,
    });
    if (!res.ok) return [];
    return res.json();
  },
  // Import the user's recurring + open TPC invoices into Income (idempotent).
  async syncTpc(): Promise<{
    imported: number;
    updated: number;
    total: number;
  }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/integrations/tpc/sync`, {
      method: "POST",
      headers,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Failed to sync TPC income");
    return data;
  },
};
